<template>
  <div class="messenger">
    <div class="messenger-tabs">
      <ul class="flex flex-row">
        <li
          :class="$classnames([
            'flex flex-row items-start flex-grow',
            'cursor-pointer bg-primary space-x-4 w-1/2',
            {
              'active': tab === 0,
            }
          ])"
          @click="tab = 0">
          <div
            class="w-6 h-6 rounded-full overflow-hidden flex-shrink-0"
            v-if="$delve(order, 'customer.photoAttach')">
            <Attachment
              class="object-cover object-center w-full h-full"
              :id="$delve(order, 'customer.photoAttach')"/>
          </div>
          <div class="flex flex-col align-self-stretch justify-end">
            <span class="text-white text-14px leading-16px">
              {{ $delve(order, 'customer.name') | fullname }}
            </span>
            <span class="text-white text-12px leading-13px font-light">Заказчик</span>
          </div>
        </li>
        <li
          :class="$classnames([
            'flex flex-row items-start flex-grow',
            'cursor-pointer bg-info space-x-4 w-1/2',
            {
              'active': tab === 1,
            }
          ])"
          @click="tab = 1">
          <div
            class="w-6 h-6 rounded-full overflow-hidden flex-shrink-0"
            v-if="$delve(order, 'contractor.photoAttach')">
            <Attachment
              class="object-cover object-center w-full h-full"
              :id="$delve(order, 'contractor.photoAttach')"/>
          </div>
          <div class="flex flex-col align-self-stretch justify-end">
            <span class="text-white text-14px leading-16px">
              {{ $delve(order, 'contractor.name') }}
            </span>
            <span class="text-white text-12px leading-13px font-light">Исполнитель</span>
          </div>
        </li>
      </ul>
    </div>

    <div class="messenger-container">
      <div
        class="messenger-content"
        ref="messenger"
        v-chat-scroll>
        <div class="messages">
          <infinite-loading
            direction="top"
            @infinite="onInfiniteLoading">
            <div slot="spinner"></div>
            <div slot="no-more"></div>
            <div slot="no-results"></div>
          </infinite-loading>

          <template v-for="(message, index) in messages">
            <div
              v-if="isDateHead(index)"
              :key="`date-${message.id || index}-${dateLabel(message.createdAt)}`"
              class="message-date">
              {{ dateLabel(message.createdAt) }}
            </div>

            <Message
              :key="`message-${message.id || index}`"
              :message="message"
              :index="index"
              :messages="messages"
              :receiver="receiver"
              @lightbox="showLightbox"/>
          </template>
        </div>
      </div>

      <div
        class="messenger-input-container p-4"
        v-if="disputeAndJoined">
        <div
          class="messenger-upload flex flex-row items-center flex-wrap px-4"
          v-if="message.attaches.length">
          <div
            class="upload-image"
            v-for="(file, index) in message.attaches"
            :key="index"
            @click="unsetAttach(file)">
            <img
              alt=""
              :src="file.blob"
              v-if="file.blob">
          </div>
        </div>

        <div class="messenger-input">
          <VueUpload
            class="prepend"
            ref="upload"
            v-model="message.attaches"
            :multiple="true"
            :maximum="10"
            @input-filter="onInputFilter">
            <img src="@/assets/icons/attach-muted.svg" alt="">
          </VueUpload>
          <input
            type="text"
            placeholder="Написать сообщение"
            v-model="message.text"
            @keyup.enter="send">
          <div
            class="append"
            @click="send">
            <img src="@/assets/icons/messenger-send.svg" alt="">
          </div>
        </div>
      </div>
    </div>

    <Lightbox
      :toggler="lightboxToggler"
      :sources="lightboxAttaches"
      :source-index="lightboxSlide"
      :key="lightboxKey"/>
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import VueUpload from 'vue-upload-component';
import InfiniteLoading from 'vue-infinite-loading';
import Lightbox from '@/components/global/Lightbox.vue';
import MessengerMessage from '@/components/Messenger/MessengerMessage.vue';
import Attachment from '@/components/global/Attachment.vue';

export default {
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  components: {
    Message: MessengerMessage,
    InfiniteLoading,
    VueUpload,
    Attachment,
    Lightbox,
  },
  data() {
    return {
      tab: 0,
      message: {
        text: '',
        attaches: [],
      },
      lightboxToggler: false,
      lightboxAttaches: [],
      lightboxSlide: 0,
      lightboxKey: null,
    };
  },
  filters: {
    fullname(s) {
      if (s) {
        return s.replace(/(\S+) (\S)\S* (\S)\S*/, '$1 $2. $3.');
      }
      return s;
    },
  },
  computed: {
    ...mapGetters({
      messengerGetter: 'orders/messenger',
    }),
    disputeAndJoined() {
      return this.order.state === 'dispute' && !!this.order.joined;
    },
    receiver() {
      switch (this.tab) {
        case 0:
          return 'customer';
        case 1:
          return 'contractor';
        default:
          return null;
      }
    },
    withWho() {
      switch (this.tab) {
        case 0:
          return 'customer';
        case 1:
          return 'contractors';
        default:
          return null;
      }
    },
    messengerPayload() {
      return this.messengerGetter(this.order.id);
    },
    payload() {
      switch (this.tab) {
        case 0:
          return _.get(this.messengerPayload, 'customer') || {};
        case 1:
          return _.get(this.messengerPayload, 'contractors') || {};
        default:
          return {};
      }
    },
    members() {
      return _.get(this.payload, 'members') || [];
    },
    contractor() {
      return null;
    },
    messages() {
      const data = _.get(this.payload, 'messages') || [];
      return data.map((message) => ({
        ...message,
        receiver: _.find(this.members, (member) => member.id === message.receiver),
        sender: _.find(this.members, (member) => member.id === message.sender) || null,
      }));
    },
  },
  methods: {
    ...mapActions({
      fetch: 'orders/ACT_FETCH_MESSAGES',
      sendMessage: 'orders/ACT_SEND_MESSAGE',
      clear: 'orders/ACT_CLEAR_MESSAGES',
    }),

    // eslint-disable-next-line consistent-return
    onInfiniteLoading($state) {
      if (!this.order) return null;

      if (this.withWho) {
        this.fetch({
          id: this.order.id,
          withWho: this.withWho,
        }).then((output) => {
          if (output.completed) {
            $state.complete();
          } else {
            $state.loaded();
          }
        });
      }
    },

    // eslint-disable-next-line consistent-return
    send() {
      if (this.message.text === '' && !this.message.attaches.length) {
        return null;
      }

      const lastMessage = _.last(this.messages);

      this.sendMessage({
        id: this.order.id,
        lastMessageId: _.get(lastMessage, 'id') || null,
        text: this.message.text,
        attaches: this.message.attaches,
        to: this.receiver,
        withWho: this.withWho,
      });

      this.message.text = '';
      this.message.attaches = [];
    },

    isDateHead(index) {
      const currentMessage = this.messages[index];
      const previousMessage = this.messages[index - 1];

      if (!currentMessage || !currentMessage.createdAt) {
        return false;
      }

      if (!previousMessage || !previousMessage.createdAt) {
        return true;
      }

      return !moment(currentMessage.createdAt).isSame(
        moment(previousMessage.createdAt),
        'day',
      );
    },

    dateLabel(createdAt) {
      if (!createdAt) {
        return '';
      }

      const date = moment(createdAt);

      if (!date.isValid()) {
        return '';
      }

      return date.format('DD MMMM YYYY');
    },

    onInputFilter(newOne, oldOne) {
      if (newOne && !oldOne) {
        // eslint-disable-next-line no-param-reassign
        newOne.blob = '';

        if ((window.URL || window.webkitURL) && URL.createObjectURL) {
          // eslint-disable-next-line no-param-reassign
          newOne.blob = URL.createObjectURL(newOne.file);
        }
      }
    },

    unsetAttach(file) {
      this.$refs.upload.remove(file);
    },

    showLightbox({ attaches, index }) {
      this.lightboxAttaches = attaches;
      this.lightboxSlide = index;

      this.$nextTick().then(() => {
        this.lightboxToggler = !this.lightboxToggler;
      });
    },
  },
  watch: {
    lightboxAttaches: {
      handler() {
        this.lightboxKey = this.$uniqid();
      },
      deep: true,
    },
    tab: {
      handler() {
        if (this.withWho) {
          this.fetch({
            id: this.order.id,
            withWho: this.withWho,
          }).then(() => {});
        }
      },
      immediate: false,
    },
  },
  beforeDestroy() {
    this.clear(this.order.id);
  },
};
</script>

<style lang="scss" scoped>
.message-date {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px 0 12px;
  color: #8a8a8a;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
}
</style>
